import type { FC } from 'react';
import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import BitPayWithdrawSetupModal from '../BitPayWithdrawSetupModal';
import BitPayWithdrawAmountModal from '../BitPayWithdrawAmountModal';
import BitPayWithdrawConfirmationModal from '../BitPayWithdrawConfirmationModal';
import {
  getAvailableBalance,
  getBitpayEmail,
  getBitpayMinimumWithdrawalAmount,
} from '../../../../../selectors/earnings';
import { parseFloatOrFallback } from '@wix/da-shared-react/pkg/EarningsCalculator';

export interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type ModalStep = 'setup' | 'amount' | 'confirmation';

const BitPayWithdrawRootModal: FC<Props> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState<ModalStep | null>(null);

  const availableBalance = useSelector(getAvailableBalance);
  const bitpayEmail = useSelector(getBitpayEmail);
  const bitpayMinimumWithdrawalAmount = useSelector(
    getBitpayMinimumWithdrawalAmount
  );

  // Determine initial step when modal opens
  useEffect(() => {
    if (isOpen && currentStep === null) {
      // Check if user needs to setup BitPay or can go directly to amount
      if (!bitpayEmail) {
        setCurrentStep('setup');
      } else {
        setCurrentStep('amount');
      }
    } else if (!isOpen) {
      setCurrentStep(null);
    }
  }, [isOpen, bitpayEmail, currentStep]);

  const handleSetupContinue = useCallback(() => {
    setCurrentStep('amount');
  }, []);

  const handleAmountContinue = useCallback(() => {
    setCurrentStep('confirmation');
  }, []);

  const handleConfirmationDone = useCallback(() => {
    setCurrentStep(null);
    onClose();
    window.location.reload();
  }, [onClose]);

  const handleClose = useCallback(() => {
    setCurrentStep(null);
    onClose();
  }, [onClose]);

  const handleCloseAndReload = useCallback(() => {
    handleClose();
    window.location.reload();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <BitPayWithdrawSetupModal
        isOpen={currentStep === 'setup'}
        requestClose={handleClose}
        onSuccess={handleSetupContinue}
        bitpayEmail={bitpayEmail}
      />
      <BitPayWithdrawAmountModal
        isOpen={currentStep === 'amount'}
        requestClose={handleClose}
        availableBalance={parseFloatOrFallback(availableBalance, 0)}
        minimumWithdrawalAmount={Number(bitpayMinimumWithdrawalAmount) || 0}
        onContinue={handleAmountContinue}
      />
      <BitPayWithdrawConfirmationModal
        isOpen={currentStep === 'confirmation'}
        requestClose={handleCloseAndReload}
        onDone={handleConfirmationDone}
      />
    </>
  );
};

BitPayWithdrawRootModal.displayName = 'BitPayWithdrawRootModal';

export default BitPayWithdrawRootModal;
